import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  qualityApi,
  type QualityCcpItem,
  type QualityCcpSummary,
  type QualitySelfcheckEntry,
  type QualitySelfcheckType,
} from '../services/quality';

const TABS: Array<{ key: QualitySelfcheckType; label: string }> = [
  { key: 'ccp', label: 'Kritiska styrpunkter (CCP)' },
  { key: 'cleaning', label: 'Städning' },
  { key: 'calibration_scale', label: 'Kalibrering våg' },
  { key: 'calibration_refractometer', label: 'Kalibrering refraktometer' },
  { key: 'pest_control', label: 'Skadedjur' },
  { key: 'templog', label: 'Templogg' },
];

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 13a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('sv-SE', { dateStyle: 'medium' }).format(date);
}

function tabLabel(type: QualitySelfcheckType): string {
  return TABS.find((tab) => tab.key === type)?.label ?? type;
}

function entryIntro(type: QualitySelfcheckType): string {
  switch (type) {
    case 'cleaning':
      return 'Snabba noteringar för Städning.';
    case 'calibration_scale':
      return 'Snabba noteringar för Kalibrering våg.';
    case 'calibration_refractometer':
      return 'Snabba noteringar för Kalibrering refraktometer.';
    case 'pest_control':
      return 'Snabba noteringar för Skadedjur.';
    default:
      return '';
  }
}

export function QualitySelfchecksPage() {
  const toast = useToast();
  const { user, canWrite } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentType = (searchParams.get('type') as QualitySelfcheckType | null) ?? 'ccp';
  const activeType = TABS.some((tab) => tab.key === currentType) ? currentType : 'ccp';
  const writeEnabled = canWrite('quality');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [onlyOver, setOnlyOver] = useState(false);
  const [ccpSummary, setCcpSummary] = useState<QualityCcpSummary | null>(null);
  const [ccpItems, setCcpItems] = useState<QualityCcpItem[]>([]);
  const [entries, setEntries] = useState<QualitySelfcheckEntry[]>([]);
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [comment, setComment] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      if (activeType === 'ccp') {
        const data = await qualityApi.ccp(onlyOver);
        setCcpSummary(data.summary);
        setCcpItems(data.items);
        setEntries([]);
      } else if (activeType === 'templog') {
        setCcpSummary(null);
        setCcpItems([]);
        setEntries([]);
      } else {
        const data = await qualityApi.selfcheckEntries(activeType);
        setEntries(data.items);
        setCcpSummary(null);
        setCcpItems([]);
      }
    } catch {
      toast.error('Kunde inte ladda egenkontroll');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [activeType, onlyOver]);

  const handleCreateEntry = async () => {
    if (activeType === 'ccp' || activeType === 'templog') {
      return;
    }
    if (!entryDate || !comment.trim()) {
      toast.error('Fyll i datum och kommentar');
      return;
    }
    setSaving(true);
    try {
      await qualityApi.createSelfcheckEntry({
        type: activeType,
        entryDate,
        comment: comment.trim(),
        userName: user?.displayName ?? undefined,
      });
      setComment('');
      toast.success('Notering sparad');
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara notering');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (id: number) => {
    setSaving(true);
    try {
      await qualityApi.deleteSelfcheckEntry(id);
      toast.success('Notering borttagen');
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ta bort notering');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateDeviation = async (containerId: number) => {
    setSaving(true);
    try {
      await qualityApi.createDeviation(containerId);
      toast.success('Avvikelse skapad');
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte skapa avvikelse');
    } finally {
      setSaving(false);
    }
  };

  const ccpStatusText = useMemo(() => {
    if (!ccpSummary) {
      return null;
    }
    return [
      { label: 'Mätta batcher (12 mån)', value: String(ccpSummary.measuredCount) },
      { label: 'Över gräns', value: String(ccpSummary.overCount) },
      { label: 'Medel %', value: ccpSummary.averageWaterPercent.toFixed(2) },
      { label: 'Högsta %', value: ccpSummary.maxWaterPercent.toFixed(2) },
    ];
  }, [ccpSummary]);

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <Link to="/quality" className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50">
          <BackIcon />
          <span>Tillbaka</span>
        </Link>

        <div className="space-y-2">
          <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-slate-950 md:text-[30px]">Egenkontroll</h1>
        </div>

        <div className="flex flex-wrap gap-2">
          {TABS.map((tab) => {
            const active = tab.key === activeType;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setSearchParams({ type: tab.key })}
                className={active
                  ? 'rounded-full bg-black px-4 py-2 text-sm font-semibold text-white'
                  : 'rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50'}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {activeType === 'ccp' ? (
        <>
          <div className="rounded-[28px] border border-slate-300 bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="text-sm text-slate-500">CCP-1: Vattenhalt vid slungning/tappning</div>
                <div className="text-[18px] font-semibold text-slate-950 md:text-[20px]">Kritisk gräns: ≤ 20%</div>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={onlyOver} onChange={(event) => setOnlyOver(event.target.checked)} />
                <span>Visa endast &gt; 20%</span>
              </label>
            </div>

            {ccpStatusText ? (
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
                {ccpStatusText.map((item) => (
                  <div key={item.label} className="rounded-2xl border border-slate-300 p-4">
                    <div className="text-sm text-slate-500">{item.label}</div>
                    <div className="mt-1 text-[18px] font-semibold text-slate-950">{item.value}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className="overflow-hidden rounded-[28px] border border-slate-300 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
            <div className="max-h-[calc(100dvh-360px)] overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr className="border-b border-slate-300 text-left text-slate-900">
                    <th className="px-4 py-3 font-semibold">Datum</th>
                    <th className="px-4 py-3 font-semibold">Bigård</th>
                    <th className="px-4 py-3 font-semibold">Slungkod</th>
                    <th className="px-4 py-3 font-semibold">Hink</th>
                    <th className="px-4 py-3 font-semibold">Vikt (kg)</th>
                    <th className="px-4 py-3 font-semibold">Vatten %</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Åtgärd</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-slate-500">Laddar egenkontroll...</td>
                    </tr>
                  ) : ccpItems.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-slate-500">Inga mätningar hittades.</td>
                    </tr>
                  ) : (
                    ccpItems.map((item) => (
                      <tr key={item.containerId} className="border-b border-slate-200 last:border-b-0">
                        <td className="px-4 py-3">{formatDate(item.extractionDate)}</td>
                        <td className="px-4 py-3">{item.apiaryName ?? '—'}</td>
                        <td className="px-4 py-3">{item.extractionCode}</td>
                        <td className="px-4 py-3">{item.hinkLabel}</td>
                        <td className="px-4 py-3">{item.qtyKg.toFixed(1)}</td>
                        <td className="px-4 py-3">{item.waterPercent.toFixed(2)}</td>
                        <td className="px-4 py-3">
                          {item.status === 'OK' ? (
                            <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">OK</span>
                          ) : (
                            <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">Hög</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {item.deviationId ? (
                            <span className="text-sm text-slate-500">
                              {item.deviationStatus === 'released' ? 'Frisläppt' : item.deviationStatus === 'closed' ? 'Stängd' : 'Öppen'}
                            </span>
                          ) : item.status === 'HIGH' && writeEnabled ? (
                            <button type="button" className="btn btn-secondary rounded-xl px-4 py-2" onClick={() => handleCreateDeviation(item.containerId)} disabled={saving}>
                              Skapa avvikelse
                            </button>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : activeType === 'templog' ? (
        <div className="rounded-[28px] border border-slate-300 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
          <div className="text-[18px] font-semibold text-slate-950">Templogg</div>
          <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-600">
            Templogg får en egen sida i nästa steg. Den här fliken finns kvar så att egenkontrollsidan matchar gamla ApisAI.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-[28px] border border-slate-300 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
            <div className="mb-3 text-sm text-slate-600">{entryIntro(activeType)}</div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[180px_minmax(0,1fr)_120px]">
              <label className="space-y-1">
                <span className="block text-sm font-medium text-slate-700">Datum</span>
                <input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={entryDate} onChange={(event) => setEntryDate(event.target.value)} />
              </label>
              <label className="space-y-1">
                <span className="block text-sm font-medium text-slate-700">Kommentar</span>
                <input type="text" value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Skriv en kort notering..." />
              </label>
              <div className="flex items-end">
                {writeEnabled ? (
                  <button type="button" className="btn btn-success h-[38px] w-full rounded-xl" onClick={handleCreateEntry} disabled={saving}>
                    Spara
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-[28px] border border-slate-300 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
            <div className="border-b border-slate-300 px-4 py-3 text-sm font-semibold text-slate-950">Senaste 20 noteringar</div>
            <div className="divide-y divide-slate-200">
              {loading ? (
                <div className="px-4 py-8 text-center text-slate-500">Laddar noteringar...</div>
              ) : entries.length === 0 ? (
                <div className="px-4 py-8 text-center text-slate-500">Inga poster ännu.</div>
              ) : (
                entries.map((entry) => (
                  <div key={entry.id} className="flex items-center gap-4 px-4 py-4">
                    <div className="w-28 shrink-0 text-sm text-slate-700">{entry.entryDate}</div>
                    <div className="min-w-0 flex-1 text-sm text-slate-900">{entry.comment}</div>
                    <div className="hidden w-48 shrink-0 text-right text-sm text-slate-500 md:block">{entry.userName ?? '—'}</div>
                    {writeEnabled ? (
                      <button type="button" className="text-danger" onClick={() => handleDeleteEntry(entry.id)} aria-label={`Ta bort ${tabLabel(activeType)}`}>
                        <TrashIcon />
                      </button>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
